import * as React from 'react';
import { OrgComponentProps } from '../typescriptHelpers';
import FormItem from 'antd/lib/form/FormItem';
import { AppPlatform, CompletedStepsTrigger, AppStoreType } from '../types';
import { useWatch } from 'antd/lib/form/Form';
import { Form, Radio } from 'antd/lib';
import { BlockingStepsSelector } from './BlockingStepsSelector';
import { StepGraphStep } from '../types/workflow';

interface CompletedStepsTriggerSettingsProps extends OrgComponentProps {
    appId: string;
    platform: AppPlatform;
    appStoreType: AppStoreType;
    stepGraphStepsMap: Record<string, StepGraphStep>;
    parentFieldName?: string | (string | number)[];
    prefixPath?: (string | number)[];
}

function resolveArchetypesToStepConfigIds(
    trigger: CompletedStepsTrigger,
    stepGraphStepsMap: Record<string, StepGraphStep>,
): string[] {
    if (!trigger.requiredSteps?.length) {
        return [];
    }
    const archetypeSet = new Set(trigger.requiredSteps);
    return Object.values(stepGraphStepsMap)
        .filter((step) => archetypeSet.has(step.archetype))
        .map((step) => step.stepConfigId);
}

const requiredStepConfigIdsValidator = async (_, values) => {
    if (!values?.length) {
        return Promise.reject(
            new Error('Please adjust your selection or select a required release steps to be complete (green).'),
        );
    }
    return Promise.resolve();
};

export const CompletedStepsTriggerSettings: React.FC<CompletedStepsTriggerSettingsProps> = ({
    appId,
    platform,
    appStoreType,
    stepGraphStepsMap,
    parentFieldName = 'completedStepsTrigger',
    prefixPath = [],
}: CompletedStepsTriggerSettingsProps) => {
    const form = Form.useFormInstance();
    const fieldPath = React.useMemo(
        () => (Array.isArray(parentFieldName) ? parentFieldName : [parentFieldName]),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [JSON.stringify(parentFieldName)],
    );
    const absoluteFieldPath = React.useMemo(
        () => [...prefixPath, ...fieldPath],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [JSON.stringify(prefixPath), fieldPath],
    );
    const trigger = useWatch<CompletedStepsTrigger>(absoluteFieldPath);
    const hasCustomRequiredSteps = useWatch<boolean>([...absoluteFieldPath, 'hasCustomRequiredSteps']);

    React.useEffect(() => {
        if (!trigger) {
            return;
        }

        const needsLegacyMigration = !trigger.requiredStepConfigIds?.length && !!trigger.requiredSteps?.length;
        if (!needsLegacyMigration) {
            return;
        }

        const migratedStepConfigIds = resolveArchetypesToStepConfigIds(trigger, stepGraphStepsMap);
        form.setFieldValue(absoluteFieldPath, {
            ...trigger,
            requiredStepConfigIds: migratedStepConfigIds,
            requiredSteps: [],
        });
    }, [form, trigger, stepGraphStepsMap, absoluteFieldPath]);

    return (
        <div style={{ paddingLeft: 16 }}>
            <FormItem
                name={[...fieldPath, 'hasCustomRequiredSteps']}
                initialValue={false}
                style={{ marginBottom: 0, marginTop: 0 }}
            >
                <Radio.Group style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <Radio value={false}>Require all previous release steps</Radio>
                    <Radio value={true}>Require only selected release steps</Radio>
                </Radio.Group>
            </FormItem>
            {hasCustomRequiredSteps && (
                <FormItem
                    required
                    name={[...fieldPath, 'requiredStepConfigIds']}
                    rules={[{ validator: requiredStepConfigIdsValidator }]}
                    style={{ marginTop: 16, marginBottom: 0 }}
                >
                    <BlockingStepsSelector
                        appId={appId}
                        platform={platform}
                        appStoreType={appStoreType}
                        stepGraphStepsMap={stepGraphStepsMap}
                    />
                </FormItem>
            )}
        </div>
    );
};
